import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../../context/GlobalContext";
import { motion } from "framer-motion";
import { useAuraVoice } from "../../hooks/useAuraVoice";
import { refactorMasterplan } from "../../services/api";
import "./Validation.css";

// Importando os assets existentes
import StarPurple from "../../assets/purple_star.svg";
import OrangeCheck from '../../assets/orange_check.svg';
import Stars from "../../assets/stars.svg";
import Logo from "../../assets/logo.svg";

const Validation = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useGlobal();
  useAuraVoice("Dá uma olhada aqui embaixo e confira se seu desafio está certo.", "default");

  // Rastreia qual botão foi selecionado
  const [clicked, setClicked] = useState(null);
  
  // Estados para edição do desafio
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(state.aiContext || "");
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleNoClick = () => {
      setClicked("no");
      setTimeout(() => {
        navigate("/problem-description");
      }, 750);
    };

  const handleYesClick = () => {
    setClicked("yes");
    setTimeout(() => {
      navigate("/describe-challenge");
    }, 750);
  };

  const handleEditClick = () => {
    setEditedText(state.aiContext);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText(state.aiContext);
  };

  const handleConfirmEdit = async () => {
    if (!editedText.trim()) return;

    try {
      setIsRegenerating(true);
      
      const refactorData = {
        masterplan_id: state.masterplan_id,
        company_context: editedText.trim()
      };

      const response = await refactorMasterplan(refactorData);
      
      // Usar a mesma action que já existe, que também limpa as seleções
      dispatch({
        type: "FIRST_DRAFT_RESPONSE",
        payload: response
      });

      // Limpar explicitamente as seleções antigas
      dispatch({
        type: "UPDATE_STATE",
        payload: {
          selected_pains: [],
          selected_goals: [],
          selected_functions: []
        }
      });

      setIsEditing(false);
      setIsRegenerating(false);
    } catch (error) {
      console.error("Erro ao regenerar desafio:", error);
      setIsRegenerating(false);
      // Mantém o modal aberto em caso de erro
    }
  };

  return (
    <motion.div
              className="validation-container"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
      <img src={Stars} alt="Estrelas no céu" className="stars-background" />
      <main className="validation-content">
        <h1 className="validation-title">O desafio está certo?</h1>

        <div className="validation-card">
          <button className="edit-icon-button" onClick={handleEditClick}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" 
                stroke="#EC4E22" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" 
                stroke="#EC4E22" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="card-header">
            <div className="card-logo-container">
                <img src={Logo} alt="Logo" className="card-logo" />
            </div>
            <div className="card-text-container">
              <p className="card-intro-text">
                {state.aiContext}
              </p>
            </div>
          </div>
        </div>

        <div className="buttons-container">
          <button className={`no-button ${clicked === "no" ? "selected" : ""}`} onClick={handleNoClick}>
            {clicked === "no" && (
              <img
                src={OrangeCheck}
                alt="Check"
                className="yes-button-icon"
              />
            )}
            Não, refazer
          </button>
          <button className={`yes-button ${clicked === "yes" ? "selected" : ""}`} onClick={handleYesClick}>
            <img
              src={clicked === "yes" ? OrangeCheck : StarPurple}
              alt="Estrela roxa"
              className="yes-button-icon"
            />
            Confirmar
          </button>
        </div>
      </main>

      {/* Modal de edição */}
      {isEditing && (
        <motion.div 
          className="edit-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            className="edit-modal"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="edit-modal-title">
              {isRegenerating ? "Regenerando desafio..." : "Editar Desafio"}
            </h3>
            <textarea
              className="edit-textarea"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder="Descreva seu desafio..."
              rows="6"
              disabled={isRegenerating}
            />
            <div className="edit-modal-buttons">
              <button 
                className="edit-cancel-button" 
                onClick={handleCancelEdit}
                disabled={isRegenerating}
              >
                Cancelar
              </button>
              <button 
                className="edit-confirm-button" 
                onClick={handleConfirmEdit}
                disabled={!editedText.trim() || isRegenerating}
              >
                {isRegenerating ? "Regenerando..." : "Confirmar"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Validation;
